/* eslint-disable react-hooks/exhaustive-deps */

import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useContext,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import jwt_decode from "jwt-decode";

import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import CircularProgress from "@mui/material/CircularProgress";

import Header from "../../components/Header";
import Footer from "../../components/Footer";
import Disclaimer from "../../components/common/Disclaimer";
import { CustomTable } from "@toorak/tc-common-fe-sdk";
import {
  newStyles,
  overRideStyles,
} from "../../components/common/Table/TableStyle";
import { TableColumns } from "../../components/common/Table/TableColumns";

import DownArrow from "../../assets/DownArrow.svg";
import UpArrow from "../../assets/UpArrow.svg";
import EmptyLoan from "../../assets/EmptyLoan.svg";

import {
  filterKeyOrgMap as orgMap,
  filterKeyLatMap as latMap,
  filterKeyILPMap as iLPMap,
  filterKeyApraisalMap as appraisalMap,
  isILP,
  sanitizeData,
  getCookie,
  sanitizePercentage,
} from "../../utils/Common";

import { ObjectType } from "../../types/tableTypes";
import { RootContext } from "../../context/RootContext";

import "./listView.css";
import { filterDataResponse, postRowData } from "../../services/network";
import { SizingTypeValue } from "../../types/enums";
import TablePagination, {
  tablePaginationClasses as classes,
} from "@mui/material/TablePagination";
import Pagination from "@mui/material/Pagination";
import PaginationItem from "@mui/material/PaginationItem";
import { styled } from "@mui/system";
import { blue, grey } from "@mui/material/colors";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import {
  Box,
  Tab,
  TableContainer,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { WaiverStatus } from "../../components/waivers/utils";
import { EscalationTarget, Roles } from "../../utils/enums";

const headClass = {
  background: "#E4EAFF",
  color: "var(--New-Colors-BTN, #362866)",
  fontFamily: "Poppins",
  fontSize: "16px",
  fontStyle: "normal",
  fontWeight: 500,
  lineHeight: "normal",
  borderRadius: "none",
  borderRight: "1px solid #BFBFBF",
};

const CustomTablePagination = styled(TablePagination)(
  ({ theme }) => `
  & .${classes.spacer} {
    display: none;
  }

  & .${classes.toolbar} {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;

    @media (min-width: 768px) {
      flex-direction: row;
      align-items: center;
    }
  }

  & .${classes.selectLabel} {
    margin: 0;
  }

  & .${classes.select} {
    padding: 4px;
    border: none;
    background-color: transparent;
  }

  & .${classes.displayedRows} {
    display: none;
    margin: 0;

    @media (min-width: 768px) {
      margin-left: auto;
    }
  }

  & .${classes.actions} {
    display: none;
    margin-left: 0px !important;
    padding: 2px;
    border-radius: 50px;
    text-align: center;
  }

  & .${classes.actions} > button {
    margin: 0 8px;
    border: transparent;
    border-radius: 2px;
    background-color: transparent;

    &:hover {
      background-color: ${theme.palette.mode === "dark" ? grey[800] : grey[50]};
    }

    &:focus {
      outline: 1px solid ${
        theme.palette.mode === "dark" ? blue[400] : blue[200]
      };
    }
  }
`,
);

const CustomPaginationItem = styled(PaginationItem)(
  () => `
  &.MuiPaginationItem-rounded.Mui-selected {
    background-color: #362866 !important;
    color: #ffffff;
  }
`,
);

function ListView() {
  const {
    showTermSheet,
    setCurrentPath,
    setTermSheetAction,
    setTermSheetData,
    dynamicConfig,
  } = useContext(RootContext);

  // ── Responsive helpers ────────────────────────────────────────────────────
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // < 600px
  // ─────────────────────────────────────────────────────────────────────────

  const [isCollapsed] = useState<boolean>(false);
  const [filtersLoader, setFiltersLoader] = useState({});
  const [selectedRows] = useState<string[]>([]);
  const [unselectedLoanIds] = useState<string[]>([]);
  const [allRowsSelected] = useState(false);
  const [rowsData, setRowsData] = useState<any>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [totalDataCount, setTotalDataCount] = useState<number>(0);
  const [searchText, setSearchText] = useState<string>("");
  const [newestLoanArrow, setNewestLoanArrow] = useState<boolean>(false);
  const [selectedLoanType, setSelectedLoanType] = useState<any>([]);
  const [dateSelected, setDateSelected] = useState("");
  const [newTableColumns, setNewTableColumns] = useState<any[]>(TableColumns);
  const [filtersOptions, setFiltersOptions] = useState<any>({});
  const [tableLoading, setTableLoading] = useState<boolean>(false);
  const [tableFilterConfig, setTableFilterConfig] = useState<
    Record<string, any>
  >({});

  const checkboxRef: any = useRef([]);
  const { t } = useTranslation();
  const classes: any = newStyles();
  const overRidenStyles = overRideStyles();
  const navigate = useNavigate();

  const sortConfig: any = {};

  // jwt_decode throws on an empty/malformed token — landing here before the
  // access-token cookie is actually set (e.g. a role now redirected straight
  // here that never rendered this page before) used to crash the whole
  // render with a blank page and no API calls ever firing, since this ran
  // unguarded at the top of the component, ahead of any effect.
  const accessToken: string = getCookie("sizer_access_token") || "";
  let decodedData: ObjectType = {};
  try {
    decodedData = jwt_decode(accessToken);
  } catch (err) {
    console.error("Failed to decode access token", err);
  }

  const findLatestGeneratedTermsheet = (data: any[], key: string) => {
    if (!data || data?.length === 0) return null;
    if (data?.length === 1) return data[0]?.emailSentOn ? data[0] : null;

    const sortedData = [...data].sort(
      (a, b) => new Date(b[key]).getTime() - new Date(a[key]).getTime(),
    );
    const latestElements = sortedData.slice(0, 2);
    return latestElements[0]?.emailSentOn
      ? latestElements[0]
      : latestElements[1];
  };

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setCurrentPage(1);
    getRowData(1);
  }

  const handleCheckboxOnchange = (name: string, isSelected: boolean) => {
    if (isSelected) {
      setSelectedLoanType((prevItems: any) => [...prevItems, name]);
    } else {
      setSelectedLoanType((prevItems: any) =>
        prevItems.filter((item: any) => item !== name),
      );
    }
  };

  const handleRatesAsOfFilter = (selectedDate: any) => {
    if (selectedDate) {
      const formattedDate = moment(selectedDate).format("MM-DD-YYYY");
      setDateSelected(formattedDate);
    }
  };

  const uncheckAll = () => {
    const checkboxes = checkboxRef?.current.filter(
      (item: any) => item !== null,
    );
    checkboxes.forEach((checkbox: any) => {
      checkbox.checked = false;
    });
    setSelectedLoanType([]);
  };

  const filterType = {
    loanType: "select",
    productType: "select",
    ratesAsOf: "dateRange",
    waiverStatus: "select",
    createdBy: "select",
    status: "select",
    sizingType: "select",
    processorAssigned: "select",
  };

  // const getRowData = async (page: any = currentPage, sortBy?: any) => {
  //   setTableLoading(true);
  //   try {
  //     const sortValue = newestLoanArrow ? "asc" : "desc";
  //     const sortBy: any = [{ key: "createdAt", sort: sortValue }];
  //     if (isUnderwriter) {
  //       sortBy.push({
  //         key: "data.underwritingStatus",
  //         sort: "asc",
  //         customOrder: ["Pending", "Approved", "Rejected"],
  //       });
  //     }

  //     let value: any = searchText;
  //     let key = "data.applicationName";
  //     if (!isNaN(+searchText)) {
  //       key = "data.evaluationID";
  //       value = searchText;
  //     }
  //     const searchKey = { key, value };

  //     let filters: any = {
  //       "data.applicationName": { $exists: true },
  //       "data.evaluationID": { $exists: true },
  //     };

  //     if (isLoanOfficer) {
  //       filters = { ...filters, createdBy: decodedData?.name };
  //     } else if (isUnderwriter) {
  //       filters = {
  //         ...filters,
  //         "data.underwritingStatus": {
  //           $in: ["Pending", "Approved", "Rejected"],
  //         },
  //         "data.orgId": getCookie("orgId"),
  //         "data.sizingType": "Detailed Loan Sizing",
  //       };
  //     } else {
  //       filters = { ...filters, "data.orgId": getCookie("orgId") };
  //     }

  //     let ratesFilter;
  //     if (dateSelected) {
  //       ratesFilter = {
  //         "data.ratesAsOf": moment(dateSelected, "MM-DD-YYYY").format(
  //           "DD-MM-YYYY",
  //         ),
  //       };
  //     }

  //     if (dynamicConfig?.showSizingType) {
  //       filters = {
  //         ...filters,
  //         "data.sizingType": sizingTabsList[selectedSizingType],
  //       };
  //     }

  //     if (Object?.keys(tableFilterConfig)?.length) {
  //       const newFilters = generateFilterQuery(tableFilterConfig, filterType);
  //       filters = { ...filters, ...newFilters };
  //     }

  //     if (ratesFilter) {
  //       filters = { ...filters, ...ratesFilter };
  //     }

  //     const body: ObjectType = { page, limit: rowsPerPage, sortBy, filters };
  //     if (searchText.length) body.search = searchKey;

  //     const { data } = await postRowData(body);

  //     const tempResult: any = [];
  //     data?.data?.records.map((data: ObjectType) => {
  //       let currentDataObj;
  //       if (data?.data?.sizingType === SizingTypeValue?.DETAILED_LOAN_SIZING) {
  //         currentDataObj = data?.data?.detailedLoanSizingData ?? data?.data;
  //       } else if (
  //         data?.data?.sizingType === SizingTypeValue?.QUICK_LOAN_SIZING
  //       ) {
  //         currentDataObj = data?.data?.quickLoanSizingData ?? data?.data;
  //       } else {
  //         currentDataObj = data?.data;
  //       }

  //       const formattedCreatedOn = data?.createdAt
  //         ? moment(new Date(data.createdAt)).format("MM/DD/YYYY")
  //         : "";
  //       let ratesAsOf = data?.data?.ratesAsOf?.replace(/[a-zA-Z]/g, "") ?? "";
  //       ratesAsOf = ratesAsOf
  //         ? moment(ratesAsOf, "DD-MM-YYYY").format("MM/DD/YYYY")
  //         : "";

  //       const currentProperty = Array.isArray(currentDataObj?.propertyDetails)
  //         ? currentDataObj?.propertyDetails[0]
  //         : { propertyLocation: currentDataObj?.propertyLocation };

  //       const termSheetVersions = data?.termSheetVersions?.filter(
  //         (el: any) => el?.emailSentOn,
  //       );
  //       const termSheet =
  //         termSheetVersions?.length &&
  //         findLatestGeneratedTermsheet(termSheetVersions, "createdAt");

  //       const interestRate =
  //         (currentDataObj?.currentSelectedInterestRate?.interestRate ||
  //           currentDataObj?.loanEconomics?.interestRate ||
  //           currentDataObj?.loanEconomics?.originalInterestRate) ??
  //         null;

  //       tempResult.push({
  //         evaluationId: data?.data?.evaluationID || "",
  //         applicationName: data?.data?.applicationName || null,
  //         loanType: data?.data?.productType,
  //         loanAmount: sanitizeData(
  //           (currentDataObj?.loanEconomics?.originalLoanAmount ||
  //             currentDataObj?.loanEconomics?.initialLoanAmount ||
  //             currentDataObj?.loanEconomics?.borrowerRequested ||
  //             currentDataObj?.loanInformation?.borrowerRequested) ??
  //             null,
  //           true,
  //         ),
  //         interestRate: interestRate ? sanitizePercentage(interestRate) : null,
  //         createdOn: formattedCreatedOn,
  //         createdBy: data.createdBy,
  //         ratesAsOf,
  //         streetAddress: currentProperty?.propertyLocation?.address,
  //         city:
  //           currentProperty?.propertyLocation?.city ??
  //           currentDataObj?.propertyLocation?.city,
  //         postal:
  //           currentProperty?.propertyLocation?.postalCode ??
  //           currentDataObj?.propertyLocation?.city,
  //         loanId: data?.loanId,
  //         productType: data?.data?.loanType,
  //         termSheet: termSheet?.termSheetVersion
  //           ? `${t("termSheetV")}${termSheet?.termSheetVersion}`
  //           : "Not Generated",
  //         termSheetVersion: termSheet?.termSheetVersion,
  //         termSheetName: termSheet?.termSheetName,
  //         sizingType: data?.data?.sizingType ?? "--",
  //         waiverStatus: data?.data?.waiverStatus ?? "Not-Requested",
  //         escalations: data?.data?.escalations ?? {},
  //       });
  //     });

  //     setTotalDataCount(data?.data?.total);
  //     setRowsData(tempResult);
  //   } catch (err) {
  //     console.error(err);
  //   } finally {
  //     setTableLoading(false);
  //   }
  // };

  const getRowData = async (page: any = currentPage) => {
    setTableLoading(true);
    try {
      const sortValue = newestLoanArrow ? "asc" : "desc";
      // Sort by last-updated (not just created) so an escalate action
      // resurfaces the loan to the top of the relevant list/queue.
      const sortBy: any = [{ key: "updatedAt", sort: sortValue }];

      if (isUnderwriter) {
        sortBy.push({
          key: "latestEscalationStatus",          // ← agreed-upon sentinel string
          sort: "asc",
          customOrder: ["Pending"],
        });
      }
      let value: any = searchText;
      let key = "data.applicationName";
      if (!isNaN(+searchText)) {
        key = "data.evaluationID";
        value = searchText;
      }
      const searchKey = { key, value };

      let filters: any = {
        "data.applicationName": { $exists: true },
        "data.evaluationID": { $exists: true },
      };

      if (dynamicConfig?.ViteAppKcRealm.includes("merchants")) {
        filters = { ...filters, "data.sizingType": { $exists: true } };
      }

      // A loan belongs in the underwriter/credit_committee queue as soon as
      // it carries a pending per-test or general waiver (checking every
      // array entry via $elemMatch, not just the most recent one — several
      // waivers can be open on the same loan at once).
      const waiverPendingOr = [
        { "data.waiverDetails": { $elemMatch: { status: WaiverStatus.PENDING } } },
        { "data.generalWaiverDetails": { $elemMatch: { status: WaiverStatus.PENDING } } },
      ];

      if (isLoanOfficer) {
        filters = { ...filters, createdBy: decodedData?.name };
      } else if (isUnderwriter) {
        filters = {
          ...filters,
          "data.orgId": getCookie("orgId"),
          $or: [
            ...waiverPendingOr,
            {
              "data.escalations": {
                $elemMatch: { escalatedTo: EscalationTarget.underwriter },
              },
            },
          ],
        };
      } else if (isCreditCommittee) {
        filters = {
          ...filters,
          "data.orgId": getCookie("orgId"),
          $or: [
            ...waiverPendingOr,
            {
              "data.escalations": {
                $elemMatch: { escalatedTo: EscalationTarget.creditCommittee },
              },
            },
          ],

        };
      } else if (isLoanProcessor) {
        // Escalation to the processor role isn't mandatory for visibility —
        // a processor should also see loans that haven't been escalated to
        // anyone yet (e.g. fresh loans created by a loan_officer), not just
        // ones explicitly escalated to processor.
        filters = {
          ...filters,
          "data.orgId": getCookie("orgId"),
          $or: [
            {
              "data.escalations": {
                $elemMatch: { escalatedTo: EscalationTarget.processor },
              },
            },
            { "data.escalations": { $exists: false } },
            { "data.escalations": { $size: 0 } },
          ],
        };
      } else {
        filters = { ...filters, "data.orgId": getCookie("orgId") };
      }

      let ratesFilter;
      if (dateSelected) {
        ratesFilter = {
          "data.ratesAsOf": moment(dateSelected, "MM-DD-YYYY").format(
            "DD-MM-YYYY",
          ),
        };
      }

      if (Object?.keys(tableFilterConfig)?.length) {
        const newFilters = generateFilterQuery(tableFilterConfig, filterType);
        filters = { ...filters, ...newFilters };
      }

      if (ratesFilter) {
        filters = { ...filters, ...ratesFilter };
      }

      const body: ObjectType = { page, limit: rowsPerPage, sortBy, filters };
      if (searchText.length) body.search = searchKey;

      const { data } = await postRowData(body);

      const tempResult: any = [];
      data?.data?.records.map((data: ObjectType) => {
        let currentDataObj;
        if (data?.data?.sizingType === SizingTypeValue?.DETAILED_LOAN_SIZING) {
          currentDataObj = data?.data?.detailedLoanSizingData ?? data?.data;
        } else if (
          data?.data?.sizingType === SizingTypeValue?.QUICK_LOAN_SIZING
        ) {
          currentDataObj = data?.data?.quickLoanSizingData ?? data?.data;
        } else {
          currentDataObj = data?.data;
        }

        const formattedCreatedOn = data?.createdAt
          ? moment(new Date(data.createdAt)).format("MM/DD/YYYY")
          : "";
        let ratesAsOf = data?.data?.ratesAsOf?.replace(/[a-zA-Z]/g, "") ?? "";
        ratesAsOf = ratesAsOf
          ? moment(ratesAsOf, "DD-MM-YYYY").format("MM/DD/YYYY")
          : "";

        const currentProperty = Array.isArray(currentDataObj?.propertyDetails)
          ? currentDataObj?.propertyDetails[0]
          : { propertyLocation: currentDataObj?.propertyLocation };

        const termSheetVersions = data?.termSheetVersions?.filter(
          (el: any) => el?.emailSentOn,
        );
        const termSheet =
          termSheetVersions?.length &&
          findLatestGeneratedTermsheet(termSheetVersions, "createdAt");

        const interestRate =
          (currentDataObj?.currentSelectedInterestRate?.interestRate ||
            currentDataObj?.loanEconomics?.interestRate ||
            currentDataObj?.loanEconomics?.originalInterestRate) ??
          null;
        // ✅ Derive latest escalation
        const escalationsArray: any[] = Array.isArray(data?.data?.escalations)
          ? data.data.escalations
          : [];
        const latestEscalation = escalationsArray.length
          ? escalationsArray[escalationsArray.length - 1]
          : null;

        tempResult.push({
          evaluationId: data?.data?.evaluationID || "",
          applicationName: data?.data?.applicationName || null,
          loanType: data?.data?.productType,
          loanAmount: sanitizeData(
            (currentDataObj?.loanEconomics?.originalLoanAmount ||
              currentDataObj?.loanEconomics?.initialLoanAmount ||
              currentDataObj?.loanEconomics?.borrowerRequested ||
              currentDataObj?.loanInformation?.borrowerRequested) ??
              null,
            true,
          ),
          interestRate: interestRate ? sanitizePercentage(interestRate) : null,
          createdOn: formattedCreatedOn,
          createdBy: data.createdBy,
          ratesAsOf,
          streetAddress: currentProperty?.propertyLocation?.address,
          city:
            currentProperty?.propertyLocation?.city ??
            currentDataObj?.propertyLocation?.city,
          postal:
            currentProperty?.propertyLocation?.postalCode ??
            currentDataObj?.propertyLocation?.city,
          loanId: data?.loanId,
          productType: data?.data?.loanType,
          termSheet: termSheet?.termSheetVersion
            ? `${t("termSheetV")}${termSheet?.termSheetVersion}`
            : "Not Generated",
          termSheetVersion: termSheet?.termSheetVersion,
          termSheetName: termSheet?.termSheetName,
          sizingType: data?.data?.sizingType ?? "--",
          waiverStatus: data?.data?.waiverStatus ?? "Not-Requested",
          escalations: escalationsArray, // ✅ always an array
          underwritingStatus: latestEscalation?.underwritingStatus ?? null, // ✅ flat field
          processorAssigned: data?.data?.processorAssigned ?? null,
        });
      });

      // Client-side sort: loans with a pending escalation surface first
      if (isUnderwriter) {
        tempResult.sort(
          (a: any, b: any) =>
            (a.underwritingStatus ? 0 : 1) - (b.underwritingStatus ? 0 : 1),
        );
      }

      setTotalDataCount(data?.data?.total);
      setRowsData(tempResult);
    } catch (err) {
      console.error(err);
    } finally {
      setTableLoading(false);
    }
  };

  const openEvaluateLoan = () => navigate("/evaluate-loan");

  const onLinkClick = (loanType: any, loanId: any, productType?: any) => {
    const redirectLoanType =
      loanType === "Bridge Loan"
        ? `${loanType}/${productType}`.replace(/ +/g, "")
        : loanType.replace(/ +/g, "");
    return `${window.location.origin}/loan/${redirectLoanType}/${loanId}`;
  };

  const handleSortArrow = () => setNewestLoanArrow(!newestLoanArrow);

  const getFilterData = (columnName: string = "productType") => {
    let filters: any = {
      "data.applicationName": { $exists: true },
      "data.evaluationID": { $exists: true },
    };
    if (isLoanOfficer) {
      filters = { ...filters, createdBy: decodedData?.name };
    } else {
      filters = { ...filters, "data.orgId": getCookie("orgId") };
    }

    // processorAssigned is stored as an object ({name, assignedOn}), so the
    // distinct-values aggregation has to target its nested "name" field
    // rather than the object itself.
    const propertyPath =
      columnName === "processorAssigned" ? "processorAssigned.name" : columnName;

    filterDataResponse(filters, propertyPath, columnName !== "createdBy")
      .then((res: any) => {
        const fRes: any = [];
        if (res?.data?.data) {
          Object.values(res.data.data).forEach((data: any) => {
            if (data._id) {
              fRes.push({ key: data._id, docCount: 1 });
            }
          });
          // Loans with no processorAssigned at all won't show up as a
          // distinct name, so offer "Unassigned" as its own filter option.
          if (columnName === "processorAssigned") {
            fRes.push({ key: "Unassigned", docCount: 1 });
          }
          setFiltersOptions((previous: any) => ({
            ...previous,
            [columnName]: fRes,
          }));
          setFiltersLoader((filtersLoader) => ({
            ...filtersLoader,
            [columnName]: false,
          }));
        }
      })
      .catch((err: ObjectType) => console.error(err));
  };

  // Builds the $and clause for a "dateRange" filter, or null if the range
  // isn't fully specified. Extracted from generateFilterQuery to keep its
  // complexity low.
  function buildDateRangeFilterClause(mappedKey: string, filteredValues: any[]) {
    const dateRange = filteredValues[0];
    if (!(dateRange?.gte && dateRange?.lte)) return null;
    return {
      [mappedKey]: {
        $gte: moment(dateRange.gte, "YYYY-MM-DD").format("DD-MM-YYYY"),
        $lte: moment(dateRange.lte).format("DD-MM-YYYY"),
      },
    };
  }

  // Builds the $and clause matching only the latest escalation's
  // underwritingStatus. Extracted from generateFilterQuery to keep its
  // complexity low.
  function buildStatusFilterClause(filteredValues: any[]) {
    // escalations is an array — match only the last element's underwritingStatus
    return {
      $expr: {
        $in: [
          { $arrayElemAt: ["$data.escalations.underwritingStatus", -1] },
          filteredValues,
        ],
      },
    };
  }

  // Builds the $and clause for a plain "select" filter. Extracted from
  // generateFilterQuery to keep its complexity low.
  // "Not-Requested" (waiverStatus) and "Unassigned" (processorAssigned) are
  // sentinel option values standing in for the field being absent entirely,
  // rather than a real value to match.
  function buildSelectFilterClause(mappedKey: string, filteredValues: any[]) {
    return {
      $or: filteredValues.map((val) => ({
        [mappedKey]:
          val === "Not-Requested" || val === "Unassigned"
            ? { $exists: false }
            : val,
      })),
    };
  }

  // Picks which clause builder applies for this key/type, mirroring the
  // original if / else-if priority (dateRange, then status, then select).
  function buildFilterClauseForKey(
    key: string,
    type: any,
    mappedKey: string,
    filteredValues: any,
  ) {
    if (type === "dateRange" && Array.isArray(filteredValues)) {
      return buildDateRangeFilterClause(mappedKey, filteredValues);
    }
    if (key === "status" && Array.isArray(filteredValues) && filteredValues.length > 0) {
      return buildStatusFilterClause(filteredValues);
    }
    if (
      type === "select" &&
      Array.isArray(filteredValues) &&
      filteredValues.length > 0
    ) {
      return buildSelectFilterClause(mappedKey, filteredValues);
    }
    return null;
  }

  function generateFilterQuery(params: any, filterType: any) {
    const filterQuery: any = { $and: [] };
    for (const [key, value] of Object.entries(params)) {
      const mappedKey =
        key === "createdBy"
          ? key
          : key === "processorAssigned"
          ? "data.processorAssigned.name"
          : `data.${key}`;
      const type = filterType[key];
      if (!type) continue;

      const filteredValues = Array.isArray(value)
        ? value.filter((val) => val !== "Select All")
        : value;

      const clause = buildFilterClauseForKey(key, type, mappedKey, filteredValues);
      if (clause) {
        filterQuery.$and.push(clause);
      }
    }
    return filterQuery;
  }

  // ⚠️  Do NOT use <Button> here — MUI Pagination already wraps each slot in
  // a <button> (PaginationItem).  Nesting a <Button> inside it produces
  // <button><button>…</button></button> which is invalid HTML and triggers a
  // React validateDOMNesting warning.  Use a plain inline <Box> instead.
  const PrevButton = () => (
    <Box
      component="span"
      data-testid="previous-button"
      sx={{ display: "flex", alignItems: "center", gap: 0.25 }}
    >
      <NavigateBeforeIcon fontSize="small" />
      <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
        Previous
      </Box>
    </Box>
  );

  const NextButton = () => (
    <Box
      component="span"
      data-testid="next-button"
      sx={{ display: "flex", alignItems: "center", gap: 0.25 }}
    >
      <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
        Next
      </Box>
      <NavigateNextIcon fontSize="small" />
    </Box>
  );

  // ── Main table view ──────────────────────────────────────────────────────
  const applicationTable = () => {
    return (
      <Box sx={{ px: { xs: 1, sm: 2, md: 3 }, py: { xs: 1, sm: 2 } }}>
        <Disclaimer
          boldText={t("elNoteText")}
          children={<span>{t("evaluateLoanDisclaimer")}</span>}
          customClass="disclaimerBox"
          showClose={true}
        />

        {/* Applications count + sort */}
        <Grid
          container
          alignItems="center"
          sx={{ mb: 1, flexWrap: "wrap", rowGap: 1 }}
        >
          <Grid
            item
            xs={12}
            sm={6}
            sx={{ display: "flex", alignItems: "center" }}
          >
            <Typography
              component="h3"
              sx={{
                fontSize: { xs: "1rem", sm: "1.17rem" },
                fontWeight: 600,
                margin: 0,
              }}
            >
              {t("applications")}
              {totalDataCount}
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: { xs: "flex-start", sm: "flex-end" },
            }}
          >
            <Typography
              component="p"
              sx={{ fontSize: { xs: "0.85rem", sm: "1rem" }, margin: 0 }}
            >
              {t("newestFirst")}
            </Typography>
            <IconButton
              size="small"
              sx={{ "&:hover": { backgroundColor: "transparent" } }}
              onClick={handleSortArrow}
              data-testid="arrow-icon"
            >
              <img
                src={newestLoanArrow ? DownArrow : UpArrow}
                alt="arrow.svg"
              />
            </IconButton>
          </Grid>
        </Grid>

        {/* Sizing type tabs */}
        {/* {dynamicConfig?.showSizingType && !isUnderwriter ? (
          <Grid item xs={12} className="sizing-tab">
            <Tabs
              value={selectedSizingType}
              onChange={handleSizingTypeChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              {sizingTabsList?.map((sizingType: string, idx: number) => (
                <Tab
                  key={sizingType}
                  label={
                    <span
                      className={
                        selectedSizingType === idx
                          ? "active-list-tab"
                          : "inactive-list-tab"
                      }
                    >
                      {sizingType}
                    </span>
                  }
                />
              ))}
            </Tabs>
          </Grid>
        ) : null} */}

        {/* ✅ Table area — swaps between loader and real table */}
        <TableContainer
          className={classes?.customScrollbar}
          sx={{
            width: "100%",
            overflowX: "auto",
            // ✅ Consistent border so the container doesn't shift when swapping
            border: "1px solid #e0e0e0",
            borderRadius: "4px",
          }}
        >
          {tableLoading ? (
            // ✅ Shows real headers + spinner in body while fetching
            <LoadingTable />
          ) : (
            <CustomTable
              columnsData={newTableColumns}
              rowsData={rowsData}
              classes={classes}
              isCollapsed={isCollapsed}
              filtersLoader={filtersLoader}
              tableFilterConfig={tableFilterConfig}
              tableSearchConfig={tableSearchConfig}
              tableSortConfig={tableSortConfig}
              filtersOptions={filtersOptions}
              onSort={onSort}
              handleFilterToggle={handleFilterToggle}
              handleCheckBoxChange={handleCheckBoxChange}
              handleDateRangeFilter={handleDateRangeFilter}
              expandColumnIndex={0}
              showNoDataMsg
              overRideStyles={{
                ...overRidenStyles,
                filterBoxStyle: { right: "0px", left: "auto", top: "40px" },
              }}
              onSelectedColor="#F6FBFF"
              showCheckBox={false}
              allSelected={allRowsSelected && unselectedLoanIds.length === 0}
              selectedLoans={selectedRows}
              fewRowsSelected={
                unselectedLoanIds?.length > 0 && selectedRows?.length > 0
              }
              disableSelectAll={isILP() ? false : !isSingleLoanTypeSelected}
              selectAllTootltipText={
                isSingleLoanTypeSelected || isILP()
                  ? ""
                  : "Select a loan type to export loans"
              }
              setCurrentPage={setCurrentPage}
              currentPage={currentPage}
              setRowsPerPage={setRowsPerPage}
              rowsPerPage={rowsPerPage}
              totalDataCount={totalDataCount}
              onLinkClick={onLinkClick}
              getRowData={getRowData}
              handleCheckboxOnchange={handleCheckboxOnchange}
              selectedLoanType={selectedLoanType}
              uncheckAll={uncheckAll}
              checkboxRef={checkboxRef}
              handleRatesAsOfFilter={handleRatesAsOfFilter}
              dateSelected={dateSelected}
              setDateSelected={setDateSelected}
            />
          )}
        </TableContainer>

        {/* Pagination — hidden while loading to avoid confusion */}
        {!tableLoading && (
          <Grid
            container
            alignItems="center"
            justifyContent="space-between"
            sx={{
              mt: { xs: 1, sm: 0 },
              mb: dynamicConfig?.ViteAppKcRealm === "bplsizer" ? 3 : 1,
              flexWrap: "wrap",
              rowGap: 1,
            }}
          >
            <Grid item xs={12} sm={6}>
              <CustomTablePagination
                rowsPerPageOptions={[5, 10, 25]}
                count={totalDataCount}
                rowsPerPage={rowsPerPage}
                colSpan={3}
                page={currentPage}
                onPageChange={(event: unknown, newPage: number) =>
                  setCurrentPage(newPage)
                }
                onRowsPerPageChange={(
                  event: React.ChangeEvent<HTMLInputElement>,
                ) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setCurrentPage(1);
                }}
                style={{ borderBottom: "none" }}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={6}
              sx={{
                display: "flex",
                justifyContent: { xs: "center", sm: "flex-end" },
              }}
            >
              <Pagination
                size={isMobile ? "small" : "medium"}
                count={Math.ceil(totalDataCount / rowsPerPage)}
                page={currentPage}
                onChange={(event: unknown, newPage: number) =>
                  setCurrentPage(newPage)
                }
                siblingCount={isMobile ? 0 : 1}
                boundaryCount={1}
                renderItem={(item) => (
                  <CustomPaginationItem
                    {...item}
                    shape="rounded"
                    slots={{ previous: PrevButton, next: NextButton }}
                  />
                )}
              />
            </Grid>
          </Grid>
        )}
      </Box>
    );
  };
  // ─────────────────────────────────────────────────────────────────────────

  // ── Empty state ────────────────────────────────────────────────────────
  function noApplicationData() {
    return (
      // ✅ Replaced offset-based Grid with a centered flex column — works on all screens
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          px: { xs: 2, sm: 6, md: 10 },
          py: { xs: 4, sm: 6 },
          gap: 3,
        }}
      >
        <Box
          component="img"
          src={EmptyLoan}
          alt="empty-loan"
          // ✅ Image scales down on small screens instead of overflowing
          sx={{ maxWidth: { xs: "80%", sm: "60%", md: "40%" }, height: "auto" }}
        />

        <Typography
          variant="h5"
          sx={{ fontSize: { xs: "1.1rem", sm: "1.5rem" } }}
        >
          {t("noApplicationText")}
        </Typography>

        <Button
          variant="contained"
          onClick={openEvaluateLoan}
          data-testid="evaluate-new-loan-button"
        >
          {t("evaluateNewLoan")}
        </Button>

        <Box sx={{ width: "100%", mt: 2 }}>
          <Footer showFooterText={true} />
        </Box>
      </Box>
    );
  }
  // ─────────────────────────────────────────────────────────────────────────

  // ── Empty Table Component ────────────────────────────────────────────────
  const LoadingTable = () => {
    return (
      <Table>
        {/* ✅ Renders actual column headers so the layout doesn't jump */}
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: "#f5f5f5", // match your existing table header bg
            }}
          >
            {newTableColumns.map((col: any) => (
              <TableCell
                key={col?.id ?? col?.key ?? col?.label ?? col?.title}
                sx={{
                  ...headClass,
                  padding: "8px",
                  width: col?.width || "auto",
                }}
              >
                {col?.label ?? col?.title ?? col?.header ?? ""}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        {/* ✅ Single full-width cell containing the spinner */}
        <TableBody sx={{ backgroundColor: "#F8F9FD" }}>
          <TableRow>
            <TableCell
              colSpan={newTableColumns.length}
              sx={{
                textAlign: "center",
                py: { xs: 6, sm: 8 },
                border: "none",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <CircularProgress size={40} thickness={4} />
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", fontSize: "0.85rem" }}
                >
                  Loading data...
                </Typography>
              </Box>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    );
  };
  // ─────────────────────────────────────────────────────────────────────────

  // ─────────────────────────── Use Callbacks ───────────────────────────────
  const handleDateRangeFilter = useCallback(
    (config: any) => {
      const colName = Object.keys(config)[0];
      const isColFiltered = config?.[colName] ?? [];
      const filterselections: any = [...isColFiltered];
      const currentSelections = { ...tableFilterConfig };

      if (!filterselections.length) {
        delete currentSelections?.[colName];
        setTableFilterConfig(currentSelections);
      } else {
        filterselections.range = true;
        setTableFilterConfig({
          ...tableFilterConfig,
          [colName]: filterselections,
        });
      }
    },
    [tableFilterConfig],
  );

  const handleCheckBoxChange = useCallback(
    (config: any) => {
      const dynamicKey = Object.keys(config)[0];
      config[Object.keys(config)[0]] = config[Object.keys(config)[0]].filter(
        (val: string) => val !== "Select All",
      );
      let updatedSelectedFiltersConfig = {};
      if (config?.[dynamicKey]?.length) {
        updatedSelectedFiltersConfig = { ...tableFilterConfig, ...config };
      } else {
        const filters: any = { ...tableFilterConfig };
        delete filters[dynamicKey];
        updatedSelectedFiltersConfig = { ...filters };
      }
      setTableFilterConfig(updatedSelectedFiltersConfig);
    },
    [tableFilterConfig],
  );

  const onSort = useCallback((config: any) => {
    const columnKey = Object.keys(config)?.[0];
  }, []);

  const handleFilterToggle = useCallback((columnName: string) => {
    if (columnName === "waiverStatus") {
      const waiverStatusFilterOptions = [
        { key: "Requested", docCount: 1 },
        { key: "Not-Requested", docCount: 1 },
        { key: "Approved", docCount: 1 },
        { key: "Rejected", docCount: 1 },
      ];
      setFiltersOptions((previous: any) => ({
        ...previous,
        [columnName]: waiverStatusFilterOptions,
      }));
    } else if (columnName === "status") {
      const escalationStatusFilterOptions = [
        { key: "Pending", docCount: 1 },
      ];
      setFiltersOptions((previous: any) => ({
        ...previous,
        [columnName]: escalationStatusFilterOptions,
      }));
    } else {
      setFiltersLoader((filtersLoader) => ({
        ...filtersLoader,
        [columnName]: true,
      }));
      getFilterData(columnName);
    }
  }, []);
  // ────────────────────────────────────────────────────────────────────────

  // ─────────── Use Memos───────────────────────────────────────────────────
  const hasData = useMemo(() => {
    return (
      rowsData.length ||
      totalDataCount ||
      !selectedLoanType.length ||
      !dateSelected
    );
  }, [rowsData, totalDataCount, selectedLoanType, dateSelected]);

  const userPermissions = useMemo(() => {
    return JSON.parse(getCookie("userPermissions") || "[]");
  }, []);

  const isLoanOfficer = useMemo(
    () => userPermissions.includes(Roles.loanOfficer),
    [userPermissions],
  );
  const isUnderwriter = useMemo(
    () => userPermissions.includes(Roles.underwriter),
    [userPermissions],
  );
  const isLoanProcessor = useMemo(
    () => userPermissions.includes(Roles.loanProcessor),
    [userPermissions],
  );
  const isCreditCommittee = useMemo(
    () => userPermissions.includes(Roles.creditCommittee),
    [userPermissions],
  );

  const tableSearchConfig = useMemo(() => {
    const filters = tableFilterConfig?.filterSelections ?? {};
    const filterKeys = Object.keys(filters);
    let tableSearchFilters = {};
    filterKeys.forEach((key: string) => {
      if (key.includes("_searchBox")) {
        const filterValue = filters?.[key].join();
        tableSearchFilters = {
          ...tableSearchFilters,
          [key.replace("_searchBox", "")]: filterValue,
        };
      }
    });
    return tableSearchFilters;
  }, [tableFilterConfig]);

  const tableSortConfig = useMemo(() => {
    return { [sortConfig?.sortColumn]: sortConfig?.sort };
  }, [sortConfig]);

  const isSingleLoanTypeSelected = useMemo(() => {
    return tableFilterConfig?.filterSelections?.["loanType"]?.length === 1;
  }, [tableFilterConfig]);
  // _______________________________________________________________________

  // ─────────── Use Effects ───────────────────────────────────────────────
  useEffect(() => {
    getRowData(currentPage);
    setCurrentPath(window.location.pathname);
  }, [
    currentPage,
    newestLoanArrow,
    rowsPerPage,
    tableFilterConfig,
    isLoanOfficer,
  ]);

  useEffect(() => {
    setTermSheetAction("");
    setTermSheetData(null);
    setNewTableColumns(
      TableColumns.filter(
        (e: any) =>
          !e?.hidden?.({
            termSheet: showTermSheet,
            merchants: dynamicConfig?.ViteAppKcRealm.includes("merchants"),
            isLoanOfficer,
            isUnderwriter,
          }),
      ),
    );
    setCurrentPath(window.location.pathname);
  }, [showTermSheet, isLoanOfficer, isUnderwriter]);
  // ──────────────────────────────────────────────────────────────────────

  return (
    // ✅ Flex column wrapper so content fills the viewport height correctly
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <Header
        showEvaluateNewLoan={Boolean(hasData) && !isUnderwriter}
        showSearch={true}
        showBorder={true}
        searchText={searchText}
        setSearchText={setSearchText}
        handleSearch={handleSearch}
      />

      {/* ✅ Main content grows to fill remaining height */}
      <Box sx={{ flex: 1 }}>
        {hasData ? applicationTable() : noApplicationData()}
      </Box>
    </Box>
  );
}

export default ListView;
